import { useEffect, useRef, useState, useCallback } from "react";
import { toast } from "sonner";
import { Play, Pause, Scissors, Repeat, RotateCcw, Save, Download, Loader2, Music } from "lucide-react";
import { getMasterStreamUrl, savePromoClip, fetchPromoClipBlob, triggerDownload } from "./adminApi";

const fmt = (s) => {
  if (s == null || isNaN(s)) return "00:00.0";
  const m = Math.floor(s / 60);
  const rem = (s % 60).toFixed(1).padStart(4, "0");
  return `${String(m).padStart(2, "0")}:${rem}`;
};
const PRESETS = [7, 10, 15, 30];

export const PromoClip = ({ slug, contentPiece, initialClip, playRef }) => {
  const [url, setUrl] = useState("");
  const [duration, setDuration] = useState(initialClip?.promo_end ? initialClip.promo_end + 5 : 0);
  const [start, setStart] = useState(initialClip?.promo_start ?? 0);
  const [end, setEnd] = useState(initialClip?.promo_end ?? 15);
  const [fadeIn, setFadeIn] = useState(initialClip?.fade_in ?? 0);
  const [fadeOut, setFadeOut] = useState(initialClip?.fade_out ?? 0);
  const [playing, setPlaying] = useState(false);
  const [loop, setLoop] = useState(true);
  const [peaks, setPeaks] = useState(null);
  const [pos, setPos] = useState(0);
  const [loadingWave, setLoadingWave] = useState(true);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [err, setErr] = useState("");

  const audioRef = useRef(null);
  const trackRef = useRef(null);
  const clipModeRef = useRef(false); // true = previewing clip (respect OUT + fades)

  // load master + waveform
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingWave(true); setErr("");
      try {
        const { url, duration_sec } = await getMasterStreamUrl(slug);
        if (cancelled) return;
        setUrl(url);
        if (duration_sec) {
          setDuration(duration_sec);
          if (!initialClip) setEnd(Math.min(15, duration_sec));
        }
        // decode for waveform peaks
        const buf = await (await fetch(url)).arrayBuffer();
        const AC = window.AudioContext || window.webkitAudioContext;
        const ctx = new AC();
        const audioBuf = await ctx.decodeAudioData(buf);
        if (cancelled) { ctx.close(); return; }
        setDuration(audioBuf.duration);
        if (!initialClip && !audioBuf.duration) {} 
        const raw = audioBuf.getChannelData(0);
        const N = 320;
        const block = Math.floor(raw.length / N);
        const pk = new Array(N);
        for (let i = 0; i < N; i++) {
          let max = 0;
          for (let j = 0; j < block; j += 64) {
            const v = Math.abs(raw[i * block + j] || 0);
            if (v > max) max = v;
          }
          pk[i] = max;
        }
        const norm = Math.max(...pk) || 1;
        setPeaks(pk.map((v) => v / norm));
        ctx.close();
      } catch (e) {
        setErr("Could not load the master. Upload a full track in the Audio tab first.");
      } finally {
        if (!cancelled) setLoadingWave(false);
      }
    })();
    return () => { cancelled = true; };
  }, [slug, contentPiece]); // eslint-disable-line

  const clampVolume = useCallback((t) => {
    // real fade in/out for clip preview
    let v = 1;
    if (fadeIn > 0 && t < start + fadeIn) v = Math.max(0, (t - start) / fadeIn);
    if (fadeOut > 0 && t > end - fadeOut) v = Math.min(v, Math.max(0, (end - t) / fadeOut));
    return Math.max(0, Math.min(1, v));
  }, [fadeIn, fadeOut, start, end]);

  const onTime = () => {
    const a = audioRef.current;
    if (!a) return;
    setPos(a.currentTime);
    if (clipModeRef.current) {
      a.volume = clampVolume(a.currentTime);
      if (a.currentTime >= end) {
        if (loop) { a.currentTime = start; a.volume = clampVolume(start); }
        else { a.pause(); setPlaying(false); }
      }
    }
  };

  const previewClip = useCallback(async () => {
    const a = audioRef.current;
    if (!a || !url) return;
    clipModeRef.current = true;
    try {
      if (Math.abs(a.currentTime - start) > 0.05) {
        a.pause();
        await new Promise((res) => {
          const done = () => { a.removeEventListener("seeked", done); res(); };
          a.addEventListener("seeked", done, { once: true });
          a.currentTime = start;
          setTimeout(res, 900); // safety if 'seeked' never fires
        });
      }
      a.volume = clampVolume(start);
      await a.play();
      setPlaying(true);
    } catch (e) {}
  }, [url, start, clampVolume]);

  useEffect(() => { if (playRef) playRef.current = previewClip; }, [playRef, previewClip]);

  const togglePlay = () => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) { a.pause(); setPlaying(false); return; }
    clipModeRef.current = false;
    a.volume = 1;
    a.play().then(() => setPlaying(true)).catch(() => {});
  };

  // drag handles on the timeline
  const dragging = useRef(null);
  const ratioFromEvent = (e) => {
    const rect = trackRef.current.getBoundingClientRect();
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    return Math.max(0, Math.min(1, x / rect.width));
  };
  const onDown = (which) => (e) => { e.preventDefault(); dragging.current = which; };
  useEffect(() => {
    const move = (e) => {
      if (!dragging.current || !duration) return;
      const t = +(ratioFromEvent(e) * duration).toFixed(1);
      if (dragging.current === "start") setStart(Math.min(t, end - 0.5));
      else setEnd(Math.max(t, start + 0.5));
    };
    const up = () => { dragging.current = null; };
    window.addEventListener("mousemove", move); window.addEventListener("mouseup", up);
    window.addEventListener("touchmove", move); window.addEventListener("touchend", up);
    return () => {
      window.removeEventListener("mousemove", move); window.removeEventListener("mouseup", up);
      window.removeEventListener("touchmove", move); window.removeEventListener("touchend", up);
    };
  }, [duration, start, end]);

  const applyPreset = (sec) => setEnd(Math.min(start + sec, duration || start + sec));
  const reset = () => { setStart(0); setEnd(Math.min(15, duration || 15)); setFadeIn(0); setFadeOut(0); };

  const save = async () => {
    setSaving(true);
    try {
      await savePromoClip(slug, {
        content_piece: contentPiece, promo_start: start, promo_end: end,
        fade_in: fadeIn, fade_out: fadeOut,
      });
      toast.success(`Promo clip saved for "${contentPiece}"`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const download = async () => {
    setDownloading(true);
    try {
      const blob = await fetchPromoClipBlob(slug, { start, end, fade_in: fadeIn, fade_out: fadeOut, content_piece: contentPiece });
      triggerDownload(blob, `fanly-${slug}-${contentPiece}-promo.mp3`);
      toast.success("Snippet downloaded");
    } catch (e) { toast.error("Download failed"); }
    finally { setDownloading(false); }
  };

  const startPct = duration ? (start / duration) * 100 : 0;
  const endPct = duration ? (end / duration) * 100 : 0;
  const posPct = duration ? (pos / duration) * 100 : 0;
  const clipDur = Math.max(0, end - start);

  return (
    <div className="rounded-2xl border border-[#FF2A2A]/25 bg-gradient-to-b from-[#0e0f16] to-[#080809] p-6" data-testid="promo-clip-module">
      <div className="flex items-center gap-2 mb-1">
        <Scissors className="w-4 h-4 text-[#FF2A2A]" />
        <h4 className="font-display font-bold tracking-tight text-lg">PROMO CLIP</h4>
      </div>
      <p className="text-xs text-[#8a8a8a] mb-5">
        Pick the exact fragment of the <span className="text-white">master</span> to promote
        <span className="text-white"> "{contentPiece}"</span> on Reels / TikTok / Shorts. This does
        not change the Release Preview.
      </p>

      {err ? (
        <p className="font-mono-x text-xs text-[#FFB020]" data-testid="promo-error">{err}</p>
      ) : loadingWave ? (
        <div className="flex items-center gap-2 text-[#737373] text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Loading master waveform…</div>
      ) : (
        <>
          {/* waveform + selection */}
          <div ref={trackRef} data-testid="promo-timeline"
            className="relative h-24 w-full rounded-lg bg-black/40 border border-white/10 overflow-hidden select-none">
            <div className="absolute inset-0 flex items-center gap-[1px] px-1">
              {peaks?.map((v, i) => {
                const inSel = duration && (i / peaks.length) * duration >= start && (i / peaks.length) * duration <= end;
                return <div key={i} style={{ height: `${Math.max(3, v * 90)}%` }}
                  className={`flex-1 rounded-full ${inSel ? "bg-[#FF2A2A]" : "bg-white/20"}`} />;
              })}
            </div>
            {/* selection overlay */}
            <div className="absolute top-0 bottom-0 bg-[#FF2A2A]/10 border-x-2 border-[#FF2A2A]"
              style={{ left: `${startPct}%`, width: `${Math.max(0, endPct - startPct)}%` }} data-testid="promo-selection" />
            {/* playhead */}
            <div className="absolute top-0 bottom-0 w-[2px] bg-white/80" style={{ left: `${posPct}%` }} />
            {/* handles */}
            <div onMouseDown={onDown("start")} onTouchStart={onDown("start")} data-testid="promo-handle-in"
              className="absolute top-0 bottom-0 w-3 -ml-1.5 cursor-ew-resize bg-[#FF2A2A] rounded"
              style={{ left: `${startPct}%` }} title="IN" />
            <div onMouseDown={onDown("end")} onTouchStart={onDown("end")} data-testid="promo-handle-out"
              className="absolute top-0 bottom-0 w-3 -ml-1.5 cursor-ew-resize bg-[#FF2A2A] rounded"
              style={{ left: `${endPct}%` }} title="OUT" />
          </div>

          {/* timecodes */}
          <div className="flex items-center justify-between mt-3 font-mono-x text-xs text-[#cfcfcf]">
            <span data-testid="promo-in-time">IN {fmt(start)}</span>
            <span className="text-[#FF2A2A] font-semibold" data-testid="promo-duration">{clipDur.toFixed(1)} sec</span>
            <span data-testid="promo-out-time">OUT {fmt(end)}</span>
          </div>

          {/* controls */}
          <div className="flex flex-wrap items-center gap-2 mt-4">
            <button data-testid="promo-play" onClick={togglePlay}
              className="flex items-center gap-2 border border-white/20 rounded-full px-4 py-2 text-sm hover:border-[#FF2A2A] transition-colors">
              {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />} Play
            </button>
            <button data-testid="promo-preview" onClick={previewClip}
              className="flex items-center gap-2 bg-[#FF2A2A] text-white rounded-full px-4 py-2 text-sm font-semibold hover:scale-[1.02] transition-transform">
              <Scissors className="w-4 h-4" /> Preview clip
            </button>
            <button data-testid="promo-loop" onClick={() => setLoop((l) => !l)}
              className={`flex items-center gap-2 rounded-full px-4 py-2 text-sm border transition-colors ${loop ? "border-[#FF2A2A] text-white" : "border-white/20 text-[#8a8a8a]"}`}>
              <Repeat className="w-4 h-4" /> Loop
            </button>
            <button data-testid="promo-reset" onClick={reset}
              className="flex items-center gap-2 border border-white/20 rounded-full px-4 py-2 text-sm hover:border-[#FF2A2A] transition-colors">
              <RotateCcw className="w-4 h-4" /> Reset
            </button>
          </div>

          {/* presets */}
          <div className="flex flex-wrap items-center gap-2 mt-3">
            <span className="font-mono-x text-[10px] tracking-widest uppercase text-[#737373]">Presets</span>
            {PRESETS.map((p) => (
              <button key={p} data-testid={`promo-preset-${p}`} onClick={() => applyPreset(p)}
                className="rounded-full border border-white/15 px-3 py-1 text-xs hover:border-[#FF2A2A] transition-colors">{p} sec</button>
            ))}
          </div>

          {/* editable times + fades */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
            <div>
              <label className="block font-mono-x text-[9px] tracking-widest uppercase text-[#737373] mb-1">Start (sec)</label>
              <input data-testid="promo-start-input" type="number" step="0.1" min="0" max={duration} value={start}
                onChange={(e) => {
                  let v = +e.target.value; if (isNaN(v)) return;
                  v = Math.max(0, duration ? Math.min(v, duration) : v);
                  setStart(v);
                  if (v >= end) setEnd(Math.min(duration || v + 0.5, v + 0.5));
                }}
                className="w-full bg-[#0e0e0e] border border-white/15 rounded-lg px-2 py-2 text-sm text-white" />
            </div>
            <div>
              <label className="block font-mono-x text-[9px] tracking-widest uppercase text-[#737373] mb-1">End (sec)</label>
              <input data-testid="promo-end-input" type="number" step="0.1" min="0" max={duration} value={end}
                onChange={(e) => {
                  let v = +e.target.value; if (isNaN(v)) return;
                  v = Math.max(0, duration ? Math.min(v, duration) : v);
                  setEnd(v);
                  if (v <= start) setStart(Math.max(0, v - 0.5));
                }}
                className="w-full bg-[#0e0e0e] border border-white/15 rounded-lg px-2 py-2 text-sm text-white" />
            </div>
            <div>
              <label className="block font-mono-x text-[9px] tracking-widest uppercase text-[#737373] mb-1">Fade In (sec)</label>
              <input data-testid="promo-fadein" type="number" step="0.1" min="0" value={fadeIn}
                onChange={(e) => setFadeIn(Math.max(0, +e.target.value))}
                className="w-full bg-[#0e0e0e] border border-white/15 rounded-lg px-2 py-2 text-sm text-white" />
            </div>
            <div>
              <label className="block font-mono-x text-[9px] tracking-widest uppercase text-[#737373] mb-1">Fade Out (sec)</label>
              <input data-testid="promo-fadeout" type="number" step="0.1" min="0" value={fadeOut}
                onChange={(e) => setFadeOut(Math.max(0, +e.target.value))}
                className="w-full bg-[#0e0e0e] border border-white/15 rounded-lg px-2 py-2 text-sm text-white" />
            </div>
          </div>

          <div className="flex flex-wrap gap-3 mt-5">
            <button data-testid="promo-save" onClick={save} disabled={saving}
              className="flex items-center gap-2 bg-white text-black font-semibold rounded-full px-6 py-2.5 hover:scale-[1.02] transition-transform disabled:opacity-60">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save promo clip
            </button>
            <button data-testid="promo-download" onClick={download} disabled={downloading}
              className="flex items-center gap-2 border border-white/20 rounded-full px-5 py-2.5 text-sm hover:border-[#FF2A2A] transition-colors disabled:opacity-60">
              {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Download snippet
            </button>
          </div>
        </>
      )}

      <audio ref={audioRef} src={url || undefined} onTimeUpdate={onTime} onEnded={() => setPlaying(false)} preload="auto" />
    </div>
  );
};
